import React, { useCallback } from 'react';
import clsx from 'clsx';
import type { TableColumn, ColumnFilters, ColumnFilterValue } from '@app-types';
import TableHeader from '@components/common/table/TableHeader';
import Pagination from '@components/common/table/Pagination';
import { LoadingState, LoadingOverlay } from '@components/common/loading';
import EmptyState from '@components/common/EmptyState';
import styles from './RZTable.module.css';

export interface RZTableSorting {
  sortConfig: { field: string; direction: 'ASC' | 'DESC' };
  onSort: (columnKey: string) => void;
  isSorted: (columnKey: string) => boolean;
}

interface RZTableProps<T> {
  // Core data
  data: T[];
  columns: TableColumn[];
  getRowKey: (item: T) => string | number;
  renderCell: (column: TableColumn, item: T) => React.ReactNode;

  // Optional sorting (integrates with TableHeader)
  sorting?: RZTableSorting;

  // Optional column filters (integrates with TableHeader)
  columnFilters?: {
    values: ColumnFilters;
    onChange: (columnKey: string, value: ColumnFilterValue | null) => void;
    onClearAll: () => void;
    hasActiveFilters: boolean;
  };

  // Optional pagination (uses Pagination component)
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
    itemName?: string;
    pageSizeOptions?: number[];
  };

  // Optional state handling (uses TableStateWrapper)
  state?: {
    isLoading: boolean;
    isFetching?: boolean;
    isEmpty: boolean;
    emptyTitle?: string;
    emptyDescription?: string;
    emptyType?: 'search' | 'default' | 'contracts' | 'meetings';
    loadingMessage?: string;
  };

  // Custom empty state component (if not provided, uses emptyTitle/emptyDescription from state)
  emptyState?: React.ReactNode;

  // Styling & behavior
  className?: string;
  rowClassName?: (item: T) => string;
  onRowClick?: (item: T) => void;

  // Advanced: Custom body for specialized tables like ScheduleTableBody
  customBody?: (data: T[]) => React.ReactNode;
}

/**
 * RZTable - Generic, reusable table component with compound component pattern
 *
 * Features:
 * - Simple API for basic tables with sorting, pagination, and loading states
 * - Compound components (RZTable.Empty, RZTable.Container, RZTable.Header, RZTable.Body) for advanced composition
 * - Reuses existing TableHeader, TableStateWrapper, and Pagination components
 * - Supports custom body rendering for specialized tables (e.g., ScheduleTableBody)
 * - When column filters are active, shows table header with filters even when empty (so user can clear filters)
 *
 * @example
 * // Simple usage with custom empty state
 * <RZTable
 *   data={contracts}
 *   columns={columns}
 *   getRowKey={(c) => c.id}
 *   renderCell={(col, contract) => <CustomCell />}
 *   sorting={{ sortConfig, onSort, isSorted }}
 *   pagination={{ currentPage, totalPages, ... }}
 *   state={{ isLoading, isEmpty }}
 *   emptyState={
 *     <RZTable.Empty
 *       icon={<FileText size={64} />}
 *       title="No contracts found"
 *       description="Try changing filters or search criteria"
 *     >
 *       <Button variant="primary">
 *         <Plus size={16} />
 *         Create Your First Contract
 *       </Button>
 *     </RZTable.Empty>
 *   }
 * />
 *
 * @example
 * // Compound usage for specialized tables
 * <RZTable.Container>
 *   <RZTable.Header columns={columns} sorting={...} />
 *   <ScheduleTableBody {...specializedProps} />
 * </RZTable.Container>
 */
const RZTable = React.memo(function RZTable<T>(props: RZTableProps<T>) {
  const {
    data,
    columns,
    getRowKey,
    renderCell,
    sorting,
    columnFilters,
    pagination,
    state,
    className,
    rowClassName,
    onRowClick,
    customBody,
    emptyState
  } = props;

  // Helper to get sort indicator for TableHeader
  const getSortIndicator = useCallback(
    (columnKey: string): string | null => {
      if (!sorting?.isSorted(columnKey)) return null;
      return sorting.sortConfig.direction === 'ASC' ? '↑' : '↓';
    },
    [sorting]
  );

  // Show loading state
  if (state?.isLoading) {
    return <LoadingState message={state.loadingMessage || 'Loading...'} />;
  }

  // Check if we have column filters (need to show filter row even when empty)
  const hasColumnFilters = columnFilters !== undefined;
  const isEmpty = state?.isEmpty && data.length === 0;
  // Check if we need extra column for sticky Clear button
  const hasActiveClearButton = columnFilters?.hasActiveFilters;
  const totalColumns = hasActiveClearButton ? columns.length + 1 : columns.length;

  // Render table with header (always show header when filters are enabled so user can clear them)
  const tableContent = (
    <div className={clsx(styles.tableCard, 'table-card')}>
      {state?.isFetching && <LoadingOverlay variant="container" />}
      <div className={clsx(styles.tableContainer, 'table-container', className)}>
        <table className={clsx(styles.table, 'table')}>
          {/* Reuse TableHeader if sorting is enabled */}
          {(() => {
            if (sorting) {
              return (
                <TableHeader
                  columns={columns}
                  onColumnSort={sorting.onSort}
                  getSortIndicator={getSortIndicator}
                  isColumnSorted={sorting.isSorted}
                  columnFilters={columnFilters}
                />
              );
            }
            if (hasColumnFilters) {
              return (
                <TableHeader
                  columns={columns}
                  onColumnSort={() => {}}
                  getSortIndicator={() => null}
                  isColumnSorted={() => false}
                  columnFilters={columnFilters}
                />
              );
            }
            return (
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      style={{
                        ...(column.width ? { width: column.width } : {}),
                        ...(column.centered ? { textAlign: 'center' } : {})
                      }}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
            );
          })()}

          {/* Show empty state in tbody if empty, or render data */}
          {(() => {
            if (isEmpty) {
              return (
                <tbody>
                  <tr>
                    <td colSpan={totalColumns} className="table-empty-cell">
                      {emptyState || (
                        <EmptyState
                          type="default"
                          title={state?.emptyTitle || 'No data found'}
                          description={state?.emptyDescription}
                        />
                      )}
                    </td>
                  </tr>
                </tbody>
              );
            }
            if (customBody) return customBody(data);
            return (
              <tbody>
                {data.map((item) => (
                  <tr
                    key={getRowKey(item)}
                    className={`table-row ${rowClassName ? rowClassName(item) : ''}`}
                    onClick={onRowClick ? () => onRowClick(item) : undefined}
                    style={onRowClick ? { cursor: 'pointer' } : undefined}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        style={column.width ? { width: column.width } : undefined}
                        className={column.centered ? 'cell-centered' : ''}
                      >
                        {renderCell(column, item)}
                      </td>
                    ))}
                    {hasActiveClearButton && <td className="table-clear-spacer" />}
                  </tr>
                ))}
              </tbody>
            );
          })()}
        </table>
      </div>

      {/* Reuse Pagination if enabled */}
      {pagination && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          pageSize={pagination.pageSize}
          onPageChange={pagination.onPageChange}
          onPageSizeChange={pagination.onPageSizeChange}
          itemName={pagination.itemName}
          pageSizeOptions={pagination.pageSizeOptions}
        />
      )}
    </div>
  );

  return tableContent;
}) as <T>(props: RZTableProps<T>) => React.ReactElement;

// Compound Components for advanced usage

/**
 * RZTable.Empty - Customizable empty state component
 * Use to show when table has no data
 */
function Empty({
  icon,
  title,
  description,
  className,
  children
}: {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className={`${styles.tableEmptyState} ${className || ''}`}>
      {icon && <div className={styles.tableEmptyStateIcon}>{icon}</div>}
      <h3 className={styles.tableEmptyStateTitle}>{title}</h3>
      {description && <p className={styles.tableEmptyStateDescription}>{description}</p>}
      {children && <div className={styles.tableEmptyStateActions}>{children}</div>}
    </div>
  );
}

/**
 * RZTable.Container - Wrapper with table-card and table-container for premium styling
 * Use when you need manual composition with specialized table bodies
 */
function Container({
  children,
  className,
  pagination
}: {
  children: React.ReactNode;
  className?: string;
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
    itemName?: string;
    pageSizeOptions?: number[];
  };
}) {
  return (
    <div className={`${styles.tableCard} table-card`}>
      <div className={`${styles.tableContainer} table-container ${className || ''}`}>
        <table className={`${styles.table} table`}>{children}</table>
      </div>
      {pagination && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          pageSize={pagination.pageSize}
          onPageChange={pagination.onPageChange}
          onPageSizeChange={pagination.onPageSizeChange}
          itemName={pagination.itemName}
          pageSizeOptions={pagination.pageSizeOptions}
        />
      )}
    </div>
  );
}

/**
 * RZTable.Header - Exposes TableHeader with sorting and column filtering support
 * Use with RZTable.Container for custom table compositions
 */
function Header({
  columns,
  sorting,
  columnFilters
}: {
  columns: TableColumn[];
  sorting?: RZTableSorting;
  columnFilters?: {
    values: ColumnFilters;
    onChange: (columnKey: string, value: ColumnFilterValue | null) => void;
    onClearAll: () => void;
    hasActiveFilters: boolean;
  };
}) {
  const getSortIndicator = (columnKey: string): string | null => {
    if (!sorting?.isSorted(columnKey)) return null;
    return sorting.sortConfig.direction === 'ASC' ? '↑' : '↓';
  };

  return (
    <TableHeader
      columns={columns}
      onColumnSort={sorting?.onSort || (() => {})}
      getSortIndicator={getSortIndicator}
      isColumnSorted={sorting?.isSorted || (() => false)}
      columnFilters={columnFilters}
    />
  );
}

/**
 * RZTable.Body - Simple tbody renderer
 * Use with RZTable.Container for basic custom table bodies
 */
function Body<T>({
  data,
  columns,
  renderCell,
  getRowKey,
  rowClassName,
  onRowClick
}: {
  data: T[];
  columns: TableColumn[];
  renderCell: (column: TableColumn, item: T) => React.ReactNode;
  getRowKey: (item: T) => string | number;
  rowClassName?: (item: T) => string;
  onRowClick?: (item: T) => void;
}) {
  return (
    <tbody>
      {data.map((item: T) => (
        <tr
          key={getRowKey(item)}
          className={`table-row ${rowClassName ? rowClassName(item) : ''}`}
          onClick={onRowClick ? () => onRowClick(item) : undefined}
          style={onRowClick ? { cursor: 'pointer' } : undefined}
        >
          {columns.map((column: TableColumn) => (
            <td key={column.key}>{renderCell(column, item)}</td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

const RZTableWithCompound = RZTable as typeof RZTable & {
  Empty: typeof Empty;
  Container: typeof Container;
  Header: typeof Header;
  Body: typeof Body;
};

RZTableWithCompound.Empty = Empty;
RZTableWithCompound.Container = Container;
RZTableWithCompound.Header = Header;
RZTableWithCompound.Body = Body;

export { RZTableWithCompound as RZTable };
